import React, { useState, useEffect, useCallback } from "react";
import { Button, DatePicker, Icon, Input, Modal, Tag, Tooltip } from "antd";
import { useDispatch } from "react-redux";
import moment from "moment";

import { convertToBRL } from "~/utils/formatters";

const LEVEL_TAGS = {
  0: { color: "green", label: "Sem aviso" },
  1: { color: "gold", label: "Nível 1 · aviso" },
  2: { color: "orange", label: "Nível 2 · ciência obrigatória" },
  3: { color: "red", label: "Nível 3 · painel bloqueado" },
};

const formatDate = (value) => (value ? moment(value).format("DD/MM/YYYY") : "");
const formatDateTime = (value) => (value ? moment(value).format("DD/MM/YYYY HH:mm") : "");
const plural = (n, singular, pluralWord) => `${n} ${n === 1 ? singular : pluralWord}`;

const styles = {
  card: {
    border: "1px solid #f0f0f0",
    borderRadius: 8,
    padding: "10px 14px",
    marginBottom: 12,
    background: "#fafafa",
  },
  row: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    flexWrap: "wrap",
  },
  left: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    flexWrap: "wrap",
    flex: "1 1 0",
    minWidth: 0,
  },
  title: { fontWeight: 600, color: "#262626", fontSize: 13 },
  muted: { color: "#8c8c8c", fontSize: 12 },
  sub: { marginTop: 6, fontSize: 12, color: "#595959" },
  ackList: {
    margin: "6px 0 0",
    padding: 0,
    listStyle: "none",
    fontSize: 12,
    color: "#595959",
  },
};

export const DefaulterNoticeCard = ({ customerId }) => {
  const dispatch = useDispatch();
  const [detail, setDetail] = useState(null);
  const [showAcks, setShowAcks] = useState(false);
  const [releaseModal, setReleaseModal] = useState(false);
  const [releasedUntil, setReleasedUntil] = useState(null);
  const [note, setNote] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const callApi = useCallback(
    (method, suffix, data, showLoading) =>
      dispatch({
        type: "DEFAULTER_NOTICE_CUSTOMER",
        payload: {
          request: {
            method,
            url: `/defaulter-notices/customers/${customerId}${suffix || ""}`,
            data,
            showLoading: !!showLoading,
          },
        },
      }).then((response) => {
        const data = response.payload.data.data;
        setDetail(data);
        return data;
      }),
    [dispatch, customerId]
  );

  useEffect(() => {
    if (!customerId) return;
    setDetail(null);
    setShowAcks(false);
    callApi("get", "", undefined, false).catch(() => {});
  }, [customerId, callApi]);

  if (!detail || !detail.status) return null;

  const status = detail.status;
  const acks = detail.acks || [];
  const release = status.release;
  const computedLevel = status.computed_level || 0;
  const isEffective = status.enabled && !release && status.level > 0;
  const tag = LEVEL_TAGS[computedLevel] || LEVEL_TAGS[0];
  // Nível só tem a cor de alerta quando o aviso está valendo de fato
  const tagColor = computedLevel === 0 || isEffective ? tag.color : undefined;

  const openReleaseModal = () => {
    setReleasedUntil(null);
    setNote("");
    setReleaseModal(true);
  };

  const submitRelease = () => {
    setIsSubmitting(true);
    callApi(
      "post",
      "/release",
      {
        released_until: releasedUntil ? releasedUntil.format("YYYY-MM-DD") : null,
        note: note || null,
      },
      true
    )
      .then(() => setReleaseModal(false))
      .catch(() => {})
      .finally(() => setIsSubmitting(false));
  };

  const revokeRelease = () => {
    Modal.confirm({
      title: "Encerrar a liberação?",
      content: "O aviso volta a aparecer no painel do cliente conforme as mensalidades em aberto.",
      okText: "Encerrar",
      okType: "danger",
      cancelText: "Cancelar",
      onOk: () => callApi("delete", "/release", undefined, true).catch(() => {}),
    });
  };

  return (
    <div style={styles.card}>
      <div style={styles.row}>
        <div style={styles.left}>
          <span style={styles.title}>Aviso de inadimplência no painel</span>
          <Tag color={tagColor} style={{ margin: 0 }}>
            {tag.label}
          </Tag>
          {status.overdue_count > 0 && (
            <span style={styles.muted}>
              {plural(status.overdue_count, "mensalidade em aberto", "mensalidades em aberto")}
              {" · "}
              {convertToBRL(status.overdue_total || 0)}
              {status.oldest_due_at ? ` · desde ${formatDate(status.oldest_due_at)}` : ""}
            </span>
          )}
        </div>
        <div style={{ display: "flex", gap: 8, flexShrink: 0 }}>
          {release && (
            <Button size="small" onClick={revokeRelease}>
              Encerrar liberação
            </Button>
          )}
          {!release && computedLevel > 0 && (
            <Button size="small" type="primary" ghost onClick={openReleaseModal}>
              <Icon type="unlock" /> Liberar (acordo)
            </Button>
          )}
        </div>
      </div>

      {!status.enabled && (
        <div style={{ ...styles.sub, color: "#8c8c8c" }}>
          <Icon type="info-circle" style={{ marginRight: 6 }} />
          Avisos desligados em Configurações › Mensalidades
        </div>
      )}

      {release && (
        <div style={styles.sub}>
          <Icon type="unlock" style={{ marginRight: 6, color: "#52c41a" }} />
          <strong>
            {release.released_until
              ? `Liberado até ${formatDate(release.released_until)}`
              : "Liberado sem prazo"}
          </strong>
          {release.note ? ` · ${release.note}` : ""}
          {release.created_by && (
            <Tooltip title={formatDateTime(release.created_at)}>
              <span style={styles.muted}> · por {release.created_by}</span>
            </Tooltip>
          )}
        </div>
      )}

      {acks.length > 0 && (
        <div style={styles.sub}>
          <Button
            type="link"
            size="small"
            style={{ padding: 0, height: "auto", fontSize: 12 }}
            onClick={() => setShowAcks(!showAcks)}
          >
            Ciências ({acks.length}) <Icon type={showAcks ? "up" : "down"} />
          </Button>
          {showAcks && (
            <ul style={styles.ackList}>
              {acks.map((ack) => (
                <li key={ack.id} style={{ padding: "2px 0" }}>
                  {ack.user}
                  {ack.email ? ` (${ack.email})` : ""}
                  {" — "}
                  {formatDateTime(ack.acknowledged_at)}
                  {" — "}
                  {plural(ack.overdue_count, "mensalidade", "mensalidades")}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      <Modal
        title="Liberar painel (acordo)"
        visible={releaseModal}
        onCancel={() => setReleaseModal(false)}
        onOk={submitRelease}
        okText="Liberar"
        cancelText="Cancelar"
        confirmLoading={isSubmitting}
        width={420}
        destroyOnClose
      >
        <p style={{ color: "#595959", marginTop: 0 }}>
          O cliente para de ver o aviso enquanto a liberação valer.
        </p>
        <div style={{ marginBottom: 12 }}>
          <div style={{ marginBottom: 6 }}>Liberar até</div>
          <DatePicker
            value={releasedUntil}
            onChange={(value) => setReleasedUntil(value)}
            format="DD/MM/YYYY"
            placeholder="Sem prazo"
            style={{ width: "100%" }}
            disabledDate={(current) => current && current.isBefore(moment().startOf("day"))}
          />
          <div style={{ ...styles.muted, marginTop: 4 }}>Vazio: liberado sem prazo</div>
        </div>
        <div>
          <div style={{ marginBottom: 6 }}>Observação</div>
          <Input.TextArea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            maxLength={500}
            autoSize={{ minRows: 2, maxRows: 4 }}
            placeholder="Ex.: acordo para pagar em 3x"
          />
        </div>
      </Modal>
    </div>
  );
};
